import * as React from "react";
import { useLocation } from "react-router";
import { useExperiments } from "~/lib/experiments/context";
import { beginTagScope, setTag } from "./clarity";
import { pushEvent } from "./data-layer";
import { EXPERIMENT_VIEW, PAGE_VIEW } from "./events";

// Mounted in Layout beside <LocalizationEntryRedirect />, which already calls
// useLocation() from exactly that position, so router context there is proven.
//
// Renders null and does all its work inside a try/catch'd useEffect. root.tsx has
// no ErrorBoundary: a throw from anything mounted in Layout takes down every route
// on the site, so nothing in this component may fail loudly — including when GTM
// and Clarity never load, which is the default in localdev and common in the wild.
//
// useEffect, not useLayoutEffect, so React 19's hoisted <title> has committed
// before we read document.title.
export function AnalyticsRouteTracker() {
  const location = useLocation();
  const { assignments, forced } = useExperiments();

  // Keyed on location.key, not pathname: `key` is fresh per history entry, so
  // this also fires on same-path re-navigation, back/forward and replace. The ref
  // makes it idempotent per entry regardless of how often the effect re-runs.
  const lastKeyRef = React.useRef<string | null>(null);
  const hasNavigatedRef = React.useRef(false);

  React.useEffect(() => {
    try {
      if (lastKeyRef.current === location.key) return;
      lastKeyRef.current = location.key;

      // Clarity tags first — they describe the page view that follows.
      beginTagScope(location.key);
      for (const [key, variant] of Object.entries(assignments)) {
        setTag(`exp_${key}`, variant);
      }
      if (forced.length > 0) {
        // So the first filter any analyst applies is `ab_forced != 1`, which is
        // what lets ?ab= pinning stay enabled in production.
        setTag("ab_forced", "1");
      }

      const navigationType = hasNavigatedRef.current ? "spa" : "initial";
      hasNavigatedRef.current = true;

      pushEvent({
        event: PAGE_VIEW,
        page_path: `${location.pathname}${location.search}`,
        page_title: typeof document === "undefined" ? "" : document.title,
        navigation_type: navigationType,
      });

      for (const [key, variant] of Object.entries(assignments)) {
        pushEvent({
          event: EXPERIMENT_VIEW,
          experiment_id: key,
          variant_id: variant,
          is_forced: forced.includes(key),
        });
      }
    } catch {
      // Never throw.
    }
  }, [location.key, location.pathname, location.search, assignments, forced]);

  return null;
}
